import { BasePanel } from "./BasePanel";
import {
  buildHelperParagraph,
  buildLabeledField,
  buildPixelBox,
  buildStudioButton,
} from "./dom";
import type { MoodBoardItem, StudioStore } from "../StudioStore";
import { openFormModal, openImageEditor, runGeneration } from "../shared";
import "../styles/moodboard.css";

interface MoodBoardPanelOptions {
  store: StudioStore;
}

/**
 * Geometry + rich payload for a tile. The store persists these natively
 * (`x/y/w/h/imageData/palette`); we mirror them on `TileExtras` so the
 * panel works in WORLD coordinates (an infinite, pan/zoom canvas) rather
 * than viewport pixels. A legacy ` mb `-sidecar in `detail` is still read
 * (so old persisted boards survive) but no longer written — `detail` is
 * now the clean note/lyric text the store feeds into `getVisualDirection`.
 */
interface TileExtras {
  /** WORLD-space top-left (px at zoom 1; may be negative — canvas is infinite). */
  x: number;
  y: number;
  w: number;
  h: number;
  /** data: URL for image tiles, or the attached visual ref on a lyric tile. */
  imageData?: string;
  /** hex strings for palette tiles. */
  palette?: string[];
}

interface Tile {
  id: string;
  kind: MoodBoardItem["kind"];
  title: string;
  /** Human-facing note/lyric text (image/palette tiles ignore this). */
  text: string;
  extras: TileExtras;
}

/** Legacy sidecar marker (read-only fallback for boards saved pre-native-fields). */
const SIDECAR = " mb ";

const TILE_DEFAULTS: Record<MoodBoardItem["kind"], { w: number; h: number }> = {
  image: { w: 220, h: 200 },
  palette: { w: 240, h: 150 },
  note: { w: 200, h: 160 },
  lyric: { w: 260, h: 190 },
};

const KIND_LABEL: Record<MoodBoardItem["kind"], string> = {
  image: "image",
  palette: "palette",
  note: "note",
  lyric: "dialogue / lyric",
};

const MIN_W = 120;
const MIN_H = 90;
/** Pointer travel (px) before a head press becomes a drag vs. a tap. */
const DRAG_THRESHOLD = 4;
const DEFAULT_PALETTE = ["#73574a", "#c9a27e", "#f1ece4", "#432818"];

// Infinite-canvas zoom envelope.
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;
const ZOOM_STEP = 1.2;
/** Padding (viewport px) kept around content when fitting to view. */
const FIT_PADDING = 72;

export class MoodBoardPanel extends BasePanel {
  private readonly store: StudioStore;

  // Compatibility composer the agent bridge drives (data-koko-action).
  private kindInputEl: HTMLSelectElement | null = null;
  private titleInputEl: HTMLInputElement | null = null;
  private detailInputEl: HTMLTextAreaElement | null = null;
  private pendingComposerImageFile: File | null = null;

  private surfaceEl: HTMLDivElement | null = null;
  /** Transformed canvas layer that holds every tile (pan/zoom target). */
  private worldEl: HTMLDivElement | null = null;
  private emptyEl: HTMLDivElement | null = null;
  private countEl: HTMLDivElement | null = null;
  private zoomLabelEl: HTMLButtonElement | null = null;
  private directionEl: HTMLDivElement | null = null;
  private fileInputEl: HTMLInputElement | null = null;
  private generateButtons: HTMLButtonElement[] = [];

  private storeUnsubscribers: (() => void)[] = [];
  /** Tiles currently rendered; keyed by id for in-place geometry updates. */
  private tileEls = new Map<string, HTMLDivElement>();
  /** Tile ids whose image is mid-generation (transient — not persisted). */
  private generatingTiles = new Set<string>();
  /** Guard: suppress re-render while WE are the one mutating the store. */
  private selfMutating = false;
  /** Highest z assigned so far (bring-to-front). */
  private zCounter = 1;
  private gestureCleanup: (() => void) | null = null;
  private generating = false;
  private didInitialFit = false;

  // Infinite-canvas viewport transform (world → screen).
  private panX = 0;
  private panY = 0;
  private zoom = 1;

  constructor(options: MoodBoardPanelOptions) {
    super();
    this.store = options.store;
  }

  // ------------------------------------------------------------------
  // DOM construction
  // ------------------------------------------------------------------

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");
    root.className = "studio-moodboard";

    root.appendChild(this.buildToolbar());
    root.appendChild(this.buildComposer());
    root.appendChild(this.buildSurface());
    root.appendChild(this.buildDirectionStrip());

    // Hidden file input reused by every image tile + the toolbar.
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.style.display = "none";
    this.fileInputEl = fileInput;
    root.appendChild(fileInput);

    return root;
  }

  private buildToolbar(): HTMLElement {
    const box = buildPixelBox({ slim: true });
    box.fill.classList.add("studio-moodboard-toolbar");

    const generate = buildStudioButton("✦ Generate board", {
      variant: "primary",
      onClick: () => void this.generateBoard(),
    });
    generate.dataset.kokoAction = "moodboard-generate";
    this.generateButtons.push(generate);

    const addImage = buildStudioButton("+ Image", {
      onClick: () => this.openComposerModal("image"),
    });
    const addPalette = buildStudioButton("+ Palette", {
      onClick: () => this.openComposerModal("palette"),
    });
    const addNote = buildStudioButton("+ Note", {
      onClick: () => this.openComposerModal("note"),
    });
    const addLyric = buildStudioButton("+ Dialogue/Lyric", {
      onClick: () => this.openComposerModal("lyric"),
    });

    const spacer = document.createElement("div");
    spacer.className = "studio-moodboard-spacer";

    // Zoom + fit controls for the infinite canvas.
    const zoomGroup = document.createElement("div");
    zoomGroup.className = "studio-moodboard-zoom";

    const zoomOut = buildStudioButton("−", {
      onClick: () => this.zoomBy(1 / ZOOM_STEP),
    });
    zoomOut.classList.add("studio-moodboard-zoom-btn");
    zoomOut.title = "Zoom out";

    const zoomLabel = buildStudioButton("100%", {
      onClick: () => this.resetView(),
    });
    zoomLabel.classList.add("studio-moodboard-zoom-label");
    zoomLabel.title = "Reset to 100%";
    this.zoomLabelEl = zoomLabel;

    const zoomIn = buildStudioButton("+", {
      onClick: () => this.zoomBy(ZOOM_STEP),
    });
    zoomIn.classList.add("studio-moodboard-zoom-btn");
    zoomIn.title = "Zoom in";

    const expand = buildStudioButton("⤢ Expand", {
      onClick: () => this.fitToView(),
    });
    expand.dataset.kokoAction = "moodboard-expand";
    expand.title = "Fit the whole board in view";

    zoomGroup.append(zoomOut, zoomLabel, zoomIn, expand);

    const count = document.createElement("div");
    count.className = "studio-moodboard-count";
    this.countEl = count;

    box.fill.append(
      generate,
      addImage,
      addPalette,
      addNote,
      addLyric,
      spacer,
      zoomGroup,
      count,
    );
    return box.outer;
  }

  /**
   * The compatibility composer. The shared `StudioKokoBridge` flies the
   * bird to and types into `moodboard-kind/title/detail` then clicks
   * `moodboard-submit`, so these controls must stay present, queryable,
   * and have a real layout box.
   */
  private buildComposer(): HTMLElement {
    const box = buildPixelBox({ slim: true });
    box.fill.classList.add("studio-moodboard-composer");
    const intro = document.createElement("div");
    intro.className = "studio-entity-create-intro";
    const copy = document.createElement("div");
    copy.appendChild(
      buildHelperParagraph(
        "Generate a starting board from your script, or pin tiles yourself. " +
          "Drag the canvas to pan, scroll to zoom, Expand to fit it all.",
      ),
    );
    const add = buildStudioButton("Pin a tile", {
      variant: "primary",
      onClick: () => this.openComposerModal(),
    });
    add.dataset.kokoAction = "moodboard-add";
    intro.append(copy, add);
    box.fill.appendChild(intro);
    return box.outer;
  }

  private openComposerModal(initialKind: MoodBoardItem["kind"] = "note"): void {
    const modal = openFormModal({
      title: "Pin to mood board",
      description: "Choose a tile type, then add the visual direction it should carry into the film.",
      surface: "moodboard-create",
      variant: "is-entity-form",
      initialFocus: '[data-koko-action="moodboard-kind"]',
      submitLabel: "Pin to board",
      submitAction: "moodboard-submit",
      cancelAction: "moodboard-create-cancel",
      onSubmit: () => this.handleComposerSubmit(),
      onClose: () => {
        this.kindInputEl = null;
        this.titleInputEl = null;
        this.detailInputEl = null;
        this.pendingComposerImageFile = null;
      },
    });

    const kind = document.createElement("select");
    kind.className = "studio-select";
    kind.dataset.kokoAction = "moodboard-kind";
    kind.innerHTML =
      '<option value="note">Visual note</option>' +
      '<option value="lyric">Dialogue / lyric anchor</option>' +
      '<option value="palette">Color palette</option>' +
      '<option value="image">Image reference</option>';
    kind.value = initialKind;
    this.kindInputEl = kind;

    const title = document.createElement("input");
    title.type = "text";
    title.className = "studio-input";
    title.placeholder = "e.g. dusk sodium-vapor alley";
    title.dataset.kokoAction = "moodboard-title";
    this.titleInputEl = title;

    const detail = document.createElement("textarea");
    detail.className = "studio-textarea";
    detail.placeholder = "Style signal + where it should appear.";
    detail.dataset.kokoAction = "moodboard-detail";
    this.detailInputEl = detail;

    const kindField = buildLabeledField("Type", kind);
    kindField.classList.add("studio-mb-field-kind");
    const titleField = buildLabeledField("Title", title);
    titleField.classList.add("studio-mb-field-title");
    const detailField = buildLabeledField("Detail", detail);
    detailField.classList.add("studio-mb-field-detail");

    const imageInput = document.createElement("input");
    imageInput.type = "file";
    imageInput.accept = "image/*";
    imageInput.className = "studio-input";
    imageInput.dataset.kokoAction = "moodboard-image-input";
    imageInput.addEventListener("change", () => {
      this.pendingComposerImageFile = imageInput.files?.[0] ?? null;
    });
    const imageField = buildLabeledField("Reference image", imageInput);
    const syncImageField = (): void => {
      imageField.hidden = kind.value !== "image";
    };
    kind.addEventListener("change", syncImageField);
    syncImageField();
    modal.body.append(kindField, titleField, detailField, imageField);
  }

  private buildSurface(): HTMLElement {
    const box = buildPixelBox();
    const surface = document.createElement("div");
    surface.className = "studio-moodboard-surface";
    surface.tabIndex = 0;
    this.surfaceEl = surface;

    // The pannable/zoomable world. Tiles live here; the empty state and
    // drop glow live on the viewport so they ignore the transform.
    const world = document.createElement("div");
    world.className = "studio-moodboard-world";
    this.worldEl = world;
    surface.appendChild(world);

    const empty = document.createElement("div");
    empty.className = "studio-moodboard-empty";
    const eTitle = document.createElement("div");
    eTitle.className = "studio-moodboard-empty-title";
    eTitle.textContent = "An empty canvas.";
    const eBody = document.createElement("div");
    eBody.className = "studio-moodboard-empty-body";
    eBody.textContent =
      "Generate a starting board from your script, or pin images, palettes, " +
      "notes, and dialogue/lyric anchors yourself. The palette + notes set the " +
      "film's visual direction — storyboard, location, and asset generation read it.";
    const eGen = buildStudioButton("✦ Generate a starting board", {
      variant: "primary",
      onClick: () => void this.generateBoard(),
    });
    eGen.classList.add("studio-moodboard-empty-generate");
    this.generateButtons.push(eGen);
    const eHint = document.createElement("div");
    eHint.className = "studio-moodboard-empty-hint";
    eHint.textContent = "drop an image anywhere · drag to pan · scroll to zoom";
    empty.append(eTitle, eBody, eGen, eHint);
    this.emptyEl = empty;
    surface.appendChild(empty);

    this.wireSurfaceDropZone(surface);
    this.wirePanZoom(surface);
    box.fill.appendChild(surface);
    return box.outer;
  }

  private buildDirectionStrip(): HTMLElement {
    const strip = document.createElement("div");
    strip.className = "studio-moodboard-direction";
    this.directionEl = strip;
    return strip;
  }

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  protected onMounted(): void {
    this.storeUnsubscribers.push(
      this.store.subscribe("moodBoardItems", () => {
        if (this.selfMutating) return;
        this.renderTiles();
      }),
    );
    this.renderTiles();
    this.applyTransform();
    this.maybeInitialFit();
  }

  protected onShown(): void {
    this.renderTiles();
    this.applyTransform();
    this.maybeInitialFit();
  }

  protected onBeforeDestroy(): void {
    this.gestureCleanup?.();
    this.gestureCleanup = null;
    for (const unsub of this.storeUnsubscribers) unsub();
    this.storeUnsubscribers = [];
  }

  /** On first reveal with content, frame the whole board once. */
  private maybeInitialFit(): void {
    if (this.didInitialFit) return;
    if (this.store.get("moodBoardItems").length === 0) return;
    this.didInitialFit = true;
    // Defer so the surface has a measured size.
    requestAnimationFrame(() => this.fitToView(false));
  }

  // ------------------------------------------------------------------
  // Store <-> Tile mapping
  // ------------------------------------------------------------------

  private readTiles(): Tile[] {
    const items = this.store.get("moodBoardItems");
    return items.map((item, idx) => this.itemToTile(item, idx));
  }

  private itemToTile(item: MoodBoardItem, idx: number): Tile {
    const native = item as MoodBoardItem & Partial<TileExtras>;
    let text = item.detail ?? "";
    let parsed: Partial<TileExtras> = {};
    // Legacy read-only fallback for boards saved before native geometry.
    const markerAt = text.indexOf(SIDECAR);
    if (markerAt !== -1) {
      const raw = text.slice(markerAt + SIDECAR.length);
      text = text.slice(0, markerAt);
      try {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === "object") parsed = obj as Partial<TileExtras>;
      } catch {
        /* corrupt sidecar — keep text only */
      }
    }
    const fallback = this.defaultGeometry(item.kind, idx);
    const extras: TileExtras = {
      x: numberOr(native.x, parsed.x, fallback.x),
      y: numberOr(native.y, parsed.y, fallback.y),
      w: numberOr(native.w, parsed.w, TILE_DEFAULTS[item.kind].w),
      h: numberOr(native.h, parsed.h, TILE_DEFAULTS[item.kind].h),
      imageData: native.imageData ?? parsed.imageData ?? undefined,
      palette: native.palette ?? parsed.palette,
    };
    return { id: item.id, kind: item.kind, title: item.title, text, extras };
  }

  private defaultGeometry(
    kind: MoodBoardItem["kind"],
    idx: number,
  ): TileExtras {
    const col = idx % 3;
    const rowN = Math.floor(idx / 3);
    return {
      x: 24 + col * 232,
      y: 20 + rowN * 176,
      w: TILE_DEFAULTS[kind].w,
      h: TILE_DEFAULTS[kind].h,
    };
  }

  // ------------------------------------------------------------------
  // Mutations
  // ------------------------------------------------------------------

  private createTile(
    kind: MoodBoardItem["kind"],
    title: string,
    text: string,
    extra: Partial<TileExtras> = {},
  ): MoodBoardItem {
    const tileCount = this.store.get("moodBoardItems").length;
    const geom = this.defaultGeometry(kind, tileCount);
    const extras: TileExtras = { ...geom, ...extra };
    this.selfMutating = true;
    let created: MoodBoardItem;
    try {
      created = this.store.addMoodBoardItem(kind, title, text, {
        x: extras.x,
        y: extras.y,
        w: extras.w,
        h: extras.h,
        imageData: extras.imageData ?? null,
        palette: extras.palette,
      });
    } finally {
      this.selfMutating = false;
    }
    this.renderTiles();
    return created;
  }

  /** Create a tile centered in the CURRENT viewport (world coords). */
  private createTileCentered(
    kind: MoodBoardItem["kind"],
    title: string,
    text: string,
    extra: Partial<TileExtras> = {},
  ): MoodBoardItem {
    const { w, h } = TILE_DEFAULTS[kind];
    const center = this.viewportCenterWorld();
    return this.createTile(kind, title, text, {
      x: Math.round(center.x - w / 2),
      y: Math.round(center.y - h / 2),
      ...extra,
    });
  }

  private persistTile(tile: Tile): void {
    this.selfMutating = true;
    try {
      this.store.updateMoodBoardItem(tile.id, {
        title: tile.title,
        detail: tile.text,
        x: tile.extras.x,
        y: tile.extras.y,
        w: tile.extras.w,
        h: tile.extras.h,
        imageData: tile.extras.imageData ?? null,
        palette: tile.extras.palette,
      });
    } finally {
      this.selfMutating = false;
    }
    this.refreshChrome();
  }

  private deleteTile(id: string): void {
    this.selfMutating = true;
    try {
      this.store.deleteMoodBoardItem(id);
    } finally {
      this.selfMutating = false;
    }
    this.generatingTiles.delete(id);
    const el = this.tileEls.get(id);
    if (el) {
      el.remove();
      this.tileEls.delete(id);
    }
    this.refreshChrome();
  }

  private async handleComposerSubmit(): Promise<boolean> {
    if (!this.kindInputEl || !this.titleInputEl || !this.detailInputEl) return false;
    const kind = this.kindInputEl.value as MoodBoardItem["kind"];
    const title = this.titleInputEl.value.trim() || defaultTitle(kind);
    const text = this.detailInputEl.value.trim();
    const extra: Partial<TileExtras> =
      kind === "palette" ? { palette: [...DEFAULT_PALETTE] } : {};
    if (kind === "image" && this.pendingComposerImageFile) {
      const dataUrl = await readFileAsDataUrl(this.pendingComposerImageFile);
      if (dataUrl) {
        extra.imageData = dataUrl;
        const palette = await extractPalette(dataUrl).catch(() => [] as string[]);
        if (palette.length > 0) {
          this.createTileCentered("palette", `${title} palette`, "", { palette });
        }
      }
    }
    this.createTileCentered(kind, title, text, extra);
    return true;
  }

  // ------------------------------------------------------------------
  // Generate-then-arrange (the board as a FIRST input)
  // ------------------------------------------------------------------

  /**
   * Produce a starting mood board from the script/treatment. The plan is
   * derived deterministically from scenes + the project title; image
   * tiles are seeded immediately in a tidy grid (with a generating
   * shimmer) and their placeholder art streams in via `runGeneration`.
   * Generate → arrange → user refines.
   */
  private async generateBoard(): Promise<void> {
    if (this.generating) return;
    this.generating = true;
    this.setGenerateState(true);
    try {
      const plan = this.buildGenerationPlan();

      // Flow every planned tile into a 3-wide grid in world space.
      const COLS = 3;
      const GAP = 28;
      const baseX = 40;
      const baseY = 40;
      const cellW = 240;
      let slot = 0;
      const place = (): { x: number; y: number } => {
        const col = slot % COLS;
        const row = Math.floor(slot / COLS);
        slot += 1;
        return { x: baseX + col * (cellW + GAP), y: baseY + row * 230 };
      };

      // Palette first (top-left anchor).
      if (plan.palette.length > 0) {
        const pos = place();
        this.createTile("palette", "Generated palette", "", {
          palette: plan.palette,
          x: pos.x,
          y: pos.y,
          w: TILE_DEFAULTS.palette.w,
          h: TILE_DEFAULTS.palette.h,
        });
      }

      // Notes / lyric anchors.
      for (const note of plan.notes) {
        const pos = place();
        this.createTile(note.kind, note.title, note.text, {
          x: pos.x,
          y: pos.y,
          w: TILE_DEFAULTS[note.kind].w,
          h: TILE_DEFAULTS[note.kind].h,
        });
      }

      // Image anchors — created as loading shimmers, art streams in.
      const imageJobs: { id: string; prompt: string }[] = [];
      for (const img of plan.images) {
        const pos = place();
        const tile = this.createTile("image", img.title, "", {
          x: pos.x,
          y: pos.y,
          w: TILE_DEFAULTS.image.w,
          h: TILE_DEFAULTS.image.h,
        });
        this.generatingTiles.add(tile.id);
        this.rerenderTileBody(tile.id);
        imageJobs.push({ id: tile.id, prompt: img.prompt });
      }

      // Frame the freshly-arranged board immediately.
      this.fitToView();

      // Resolve placeholder art in parallel; fill each tile as it lands.
      await Promise.all(
        imageJobs.map(async (job) => {
          const result = await runGeneration({
            kind: "image",
            prompt: job.prompt,
            aspect: "1:1",
            palette: plan.palette,
            seed: `moodboard:${job.id}:${job.prompt}`,
          });
          this.generatingTiles.delete(job.id);
          const tile = this.currentTile(job.id);
          if (!tile) return;
          if (result.url) tile.extras.imageData = result.url;
          this.persistTile(tile);
          this.rerenderTileBody(job.id);
        }),
      );
    } finally {
      this.generating = false;
      this.setGenerateState(false);
    }
  }

  private buildGenerationPlan(): {
    palette: string[];
    notes: { kind: "note" | "lyric"; title: string; text: string }[];
    images: { title: string; prompt: string }[];
  } {
    const scenes = this.store.get("scenes");
    const direction = this.store.getVisualDirection();
    const title = this.store.getProjectTitle();

    const palette =
      direction.palette.length > 0
        ? direction.palette.slice(0, 5)
        : cinematicPalette(title);

    const sceneSubjects = scenes
      .map((s) => sceneSubject(s.heading, s.body))
      .filter(Boolean)
      .slice(0, 5);

    const subjects =
      sceneSubjects.length > 0
        ? sceneSubjects
        : [
            "wide establishing landscape, golden hour",
            "intimate character close-up, soft key light",
            "textural detail insert, shallow depth of field",
            "moody interior, practical lighting",
          ];

    const images = subjects
      .map((subject) => ({
        title: subject.length > 28 ? `${subject.slice(0, 27)}…` : subject,
        prompt: `${title} — mood frame: ${subject}`,
      }))
      .slice(0, 6);

    const notes: { kind: "note" | "lyric"; title: string; text: string }[] = [
      {
        kind: "note",
        title: "Look & feel",
        text:
          direction.notes[0] ??
          "Handheld, 16mm grain, neon practicals. Warm-to-cool contrast.",
      },
      {
        kind: "note",
        title: "Texture",
        text: "Soft halation in highlights, deep crushed shadows, filmic grain.",
      },
    ];
    if (scenes.length > 0) {
      const line = firstDialogue(scenes);
      if (line) {
        notes.push({ kind: "lyric", title: "Dialogue anchor", text: line });
      }
    }

    return { palette, notes, images };
  }

  private setGenerateState(busy: boolean): void {
    for (const btn of this.generateButtons) {
      btn.disabled = busy;
      btn.classList.toggle("is-busy", busy);
    }
  }

  // ------------------------------------------------------------------
  // Rendering
  // ------------------------------------------------------------------

  private renderTiles(): void {
    if (!this.worldEl) return;
    const tiles = this.readTiles();
    const seen = new Set<string>();

    for (const tile of tiles) {
      seen.add(tile.id);
      let el = this.tileEls.get(tile.id);
      if (!el) {
        el = this.buildTileEl(tile);
        this.tileEls.set(tile.id, el);
        this.worldEl.appendChild(el);
        const created = el;
        requestAnimationFrame(() => created.classList.remove("is-entering"));
      } else {
        this.syncTileEl(el, tile);
      }
    }
    for (const [id, el] of this.tileEls) {
      if (!seen.has(id)) {
        el.remove();
        this.tileEls.delete(id);
      }
    }
    this.refreshChrome();
  }

  private syncTileEl(el: HTMLDivElement, tile: Tile): void {
    this.applyGeometry(el, tile.extras);
    const titleEl = el.querySelector<HTMLElement>(".studio-mb-tile-title");
    if (titleEl && titleEl !== document.activeElement) {
      titleEl.textContent = tile.title;
    }
    const body = el.querySelector<HTMLElement>(".studio-mb-tile-body");
    if (body && !body.contains(document.activeElement)) {
      body.replaceChildren();
      this.fillTileBody(body, tile);
    }
  }

  private buildTileEl(tile: Tile): HTMLDivElement {
    const el = document.createElement("div");
    el.className = `studio-mb-tile kind-${tile.kind} is-entering`;
    el.tabIndex = 0;
    el.dataset.id = tile.id;
    el.style.zIndex = String(++this.zCounter);
    this.applyGeometry(el, tile.extras);

    const head = document.createElement("div");
    head.className = "studio-mb-tile-head";

    const kindChip = document.createElement("span");
    kindChip.className = "studio-mb-tile-kind";
    kindChip.textContent = KIND_LABEL[tile.kind];

    const title = document.createElement("div");
    title.className = "studio-mb-tile-title";
    title.contentEditable = "false";
    title.spellcheck = false;
    title.textContent = tile.title;
    title.addEventListener("blur", () => {
      title.contentEditable = "false";
      const current = this.currentTile(tile.id);
      if (!current) return;
      current.title =
        (title.textContent ?? "").trim() || defaultTitle(tile.kind);
      title.textContent = current.title;
      this.persistTile(current);
    });
    title.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        title.blur();
      }
    });
    const beginTitleEdit = () => {
      title.contentEditable = "true";
      title.focus();
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(title);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    };

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "studio-mb-tile-remove";
    remove.textContent = "×";
    remove.title = "Remove tile";
    remove.dataset.kokoAction = "moodboard-remove";
    remove.dataset.id = tile.id;
    remove.addEventListener("click", () => this.deleteTile(tile.id));

    head.append(kindChip, title, remove);
    head.addEventListener("pointerdown", (ev) => {
      if (ev.target === remove) return;
      if (ev.target === title && title.isContentEditable) return;
      const onTitle = ev.target === title;
      this.beginDrag(el, tile.id, ev, onTitle ? beginTitleEdit : undefined);
    });

    const body = document.createElement("div");
    body.className = "studio-mb-tile-body";
    this.fillTileBody(body, tile);

    const resize = document.createElement("div");
    resize.className = "studio-mb-resize";
    resize.addEventListener("pointerdown", (ev) =>
      this.beginResize(el, tile.id, ev),
    );

    el.addEventListener("pointerdown", () => this.bringToFront(el));
    el.append(head, body, resize);
    return el;
  }

  private fillTileBody(body: HTMLElement, tile: Tile): void {
    switch (tile.kind) {
      case "image":
        this.fillImageBody(body, tile);
        break;
      case "palette":
        this.fillPaletteBody(body, tile);
        break;
      case "lyric":
        this.fillLyricBody(body, tile);
        break;
      default:
        this.fillTextBody(body, tile);
    }
  }

  private fillTextBody(body: HTMLElement, tile: Tile): void {
    const area = document.createElement("textarea");
    area.className = "studio-mb-note";
    area.value = tile.text;
    area.placeholder = "What this should signal to the AI…";
    area.addEventListener("blur", () => {
      const current = this.currentTile(tile.id);
      if (!current) return;
      current.text = area.value;
      this.persistTile(current);
    });
    body.appendChild(area);
  }

  /**
   * Dialogue / Lyric tile (film + music-video): the anchoring line plus
   * an OPTIONAL attached visual reference (image). The ref is stored in
   * `imageData` and is editable via the shared Photoshop editor (G7).
   */
  private fillLyricBody(body: HTMLElement, tile: Tile): void {
    const area = document.createElement("textarea");
    area.className = "studio-mb-note";
    area.value = tile.text;
    area.placeholder = "Dialogue or lyric line that anchors the mood…";
    area.addEventListener("blur", () => {
      const current = this.currentTile(tile.id);
      if (!current) return;
      current.text = area.value;
      this.persistTile(current);
    });

    const ref = document.createElement("div");
    ref.className = "studio-mb-lyric-ref";
    if (tile.extras.imageData) {
      const thumb = document.createElement("div");
      thumb.className = "studio-mb-lyric-thumb";
      const img = document.createElement("img");
      img.src = tile.extras.imageData;
      img.alt = `${tile.title} reference`;
      thumb.appendChild(img);

      const edit = document.createElement("button");
      edit.type = "button";
      edit.className = "studio-mb-ref-edit";
      edit.textContent = "Edit";
      edit.dataset.kokoAction = "moodboard-edit-image";
      edit.dataset.id = tile.id;
      edit.addEventListener("click", (ev) => {
        ev.stopPropagation();
        this.openTileImageEditor(tile.id);
      });

      const detach = document.createElement("button");
      detach.type = "button";
      detach.className = "studio-mb-ref-detach";
      detach.textContent = "×";
      detach.title = "Remove reference";
      detach.addEventListener("click", (ev) => {
        ev.stopPropagation();
        const current = this.currentTile(tile.id);
        if (!current) return;
        current.extras.imageData = undefined;
        this.persistTile(current);
        this.rerenderTileBody(tile.id);
      });

      thumb.append(edit, detach);
      ref.appendChild(thumb);
    } else {
      const attach = document.createElement("button");
      attach.type = "button";
      attach.className = "studio-mb-ref-attach";
      attach.textContent = "+ attach visual ref";
      attach.addEventListener("click", (ev) => {
        ev.stopPropagation();
        this.promptForImage(tile.id);
      });
      ref.appendChild(attach);
    }

    body.append(area, ref);
  }

  private fillImageBody(body: HTMLElement, tile: Tile): void {
    const frame = document.createElement("div");
    frame.className = "studio-mb-image";

    if (this.generatingTiles.has(tile.id)) {
      frame.classList.add("is-generating");
      const spin = document.createElement("div");
      spin.className = "studio-mb-image-spin";
      spin.textContent = "generating…";
      frame.appendChild(spin);
    } else if (tile.extras.imageData) {
      const img = document.createElement("img");
      img.src = tile.extras.imageData;
      img.alt = tile.title;
      frame.appendChild(img);

      const edit = document.createElement("button");
      edit.type = "button";
      edit.className = "studio-mb-image-edit";
      edit.textContent = "Edit";
      edit.dataset.kokoAction = "moodboard-edit-image";
      edit.dataset.id = tile.id;
      edit.addEventListener("click", (ev) => {
        ev.stopPropagation();
        this.openTileImageEditor(tile.id);
      });
      frame.appendChild(edit);
    } else {
      const drop = document.createElement("div");
      drop.className = "studio-mb-image-drop";
      drop.textContent = "drop image · click to upload";
      drop.addEventListener("click", () => this.promptForImage(tile.id));
      frame.appendChild(drop);
    }
    body.appendChild(frame);
  }

  /** Open the shared Photoshop-style editor (G7) on a tile's image. */
  private openTileImageEditor(id: string): void {
    const tile = this.currentTile(id);
    if (!tile || !tile.extras.imageData) return;
    openImageEditor({
      image: tile.extras.imageData,
      title: `Edit ${tile.title}`,
      promptPlaceholder: "Describe how you want to modify this image…",
      onChange: (next) => {
        const current = this.currentTile(id);
        if (!current) return;
        current.extras.imageData = next;
        this.persistTile(current);
        this.rerenderTileBody(id);
      },
    });
  }

  private fillPaletteBody(body: HTMLElement, tile: Tile): void {
    const wrap = document.createElement("div");
    wrap.className = "studio-mb-palette";

    const swatches = document.createElement("div");
    swatches.className = "studio-mb-swatches";
    const colors = tile.extras.palette ?? [...DEFAULT_PALETTE];

    colors.forEach((hex, i) => {
      const sw = document.createElement("div");
      sw.className = "studio-mb-swatch";
      sw.style.background = hex;

      const picker = document.createElement("input");
      picker.type = "color";
      picker.value = normalizeHex(hex);

      const hexLabel = document.createElement("span");
      hexLabel.className = "studio-mb-swatch-hex";
      hexLabel.textContent = hex;

      picker.addEventListener("input", () => {
        sw.style.background = picker.value;
        hexLabel.textContent = picker.value;
      });
      picker.addEventListener("change", () => {
        const current = this.currentTile(tile.id);
        if (!current) return;
        const next = [...(current.extras.palette ?? colors)];
        next[i] = picker.value;
        current.extras.palette = next;
        this.persistTile(current);
      });

      const rm = document.createElement("button");
      rm.type = "button";
      rm.className = "studio-mb-swatch-remove";
      rm.textContent = "×";
      rm.title = "Remove swatch";
      rm.addEventListener("click", (ev) => {
        ev.stopPropagation();
        const current = this.currentTile(tile.id);
        if (!current) return;
        const next = (current.extras.palette ?? colors).filter(
          (_, idx) => idx !== i,
        );
        current.extras.palette = next.length > 0 ? next : [...DEFAULT_PALETTE];
        this.persistTile(current);
        this.rerenderTileBody(tile.id);
      });

      sw.append(picker, hexLabel, rm);
      swatches.appendChild(sw);
    });

    const foot = document.createElement("div");
    foot.className = "studio-mb-palette-foot";
    const addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.className = "studio-mb-palette-add";
    addBtn.textContent = "+ swatch";
    addBtn.addEventListener("click", () => {
      const current = this.currentTile(tile.id);
      if (!current) return;
      current.extras.palette = [
        ...(current.extras.palette ?? colors),
        "#cccccc",
      ];
      this.persistTile(current);
      this.rerenderTileBody(tile.id);
    });
    foot.appendChild(addBtn);

    wrap.append(swatches, foot);
    body.appendChild(wrap);
  }

  private rerenderTileBody(id: string): void {
    const el = this.tileEls.get(id);
    const tile = this.currentTile(id);
    if (!el || !tile) return;
    const body = el.querySelector<HTMLElement>(".studio-mb-tile-body");
    if (!body) return;
    body.replaceChildren();
    this.fillTileBody(body, tile);
  }

  private applyGeometry(el: HTMLElement, extras: TileExtras): void {
    el.style.left = `${extras.x}px`;
    el.style.top = `${extras.y}px`;
    el.style.width = `${extras.w}px`;
    el.style.height = `${extras.h}px`;
  }

  private refreshChrome(): void {
    const count = this.store.get("moodBoardItems").length;
    if (this.emptyEl) this.emptyEl.style.display = count === 0 ? "flex" : "none";
    if (this.countEl) {
      this.countEl.textContent =
        count === 0 ? "" : `${count} tile${count === 1 ? "" : "s"}`;
    }
    this.renderDirection();
  }

  /**
   * The board's palette + notes ARE the film's visual direction. We read
   * the canonical derivation from the store (`getVisualDirection`, which
   * storyboard/location/asset panels also read) and mirror it into a
   * compact strip + an optional studio accent tint.
   */
  private renderDirection(): void {
    if (!this.directionEl) return;
    const direction = this.store.getVisualDirection();
    const { palette, notes, accent } = direction;

    this.directionEl.replaceChildren();
    const label = document.createElement("span");
    label.className = "studio-moodboard-direction-label";
    label.textContent = "Visual direction";
    this.directionEl.appendChild(label);

    if (palette.length === 0 && notes.length === 0) {
      this.directionEl.classList.add("is-empty");
      const hint = document.createElement("span");
      hint.className = "studio-moodboard-direction-notes";
      hint.textContent = "Add a palette or note to steer generation.";
      this.directionEl.appendChild(hint);
      this.clearAccent();
      return;
    }
    this.directionEl.classList.remove("is-empty");

    if (palette.length > 0) {
      const swatches = document.createElement("div");
      swatches.className = "studio-moodboard-direction-swatches";
      palette.slice(0, 8).forEach((hex) => {
        const chip = document.createElement("div");
        chip.className = "studio-moodboard-direction-chip";
        chip.style.background = hex;
        chip.title = hex;
        swatches.appendChild(chip);
      });
      this.directionEl.appendChild(swatches);
    }

    const notesEl = document.createElement("span");
    notesEl.className = "studio-moodboard-direction-notes";
    notesEl.textContent = notes.length > 0 ? notes.join(" · ") : "Palette set.";
    this.directionEl.appendChild(notesEl);

    if (accent) this.applyAccent(accent);
    else this.clearAccent();
  }

  private applyAccent(hex: string): void {
    if (typeof document === "undefined") return;
    document.documentElement.style.setProperty("--koko-visual-accent", hex);
  }

  private clearAccent(): void {
    if (typeof document === "undefined") return;
    document.documentElement.style.removeProperty("--koko-visual-accent");
  }

  // ------------------------------------------------------------------
  // Infinite-canvas pan / zoom
  // ------------------------------------------------------------------

  private wirePanZoom(surface: HTMLElement): void {
    // Drag the empty canvas to pan (tiles handle their own pointerdown).
    surface.addEventListener("pointerdown", (ev) => {
      if (ev.button !== 0) return;
      const target = ev.target as HTMLElement;
      if (target.closest(".studio-mb-tile")) return;
      if (target.closest(".studio-moodboard-empty-generate")) return;
      this.beginPan(ev);
    });

    surface.addEventListener(
      "wheel",
      (ev) => {
        ev.preventDefault();
        if (ev.ctrlKey || ev.metaKey) {
          const factor = ev.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
          this.zoomBy(factor, ev.clientX, ev.clientY);
        } else {
          this.panX -= ev.deltaX;
          this.panY -= ev.deltaY;
          this.applyTransform();
        }
      },
      { passive: false },
    );
  }

  private beginPan(ev: PointerEvent): void {
    if (!this.surfaceEl) return;
    ev.preventDefault();
    const startX = ev.clientX;
    const startY = ev.clientY;
    const originPanX = this.panX;
    const originPanY = this.panY;
    let armed = false;
    const move = (e: PointerEvent) => {
      if (!armed) {
        if (
          Math.abs(e.clientX - startX) < DRAG_THRESHOLD &&
          Math.abs(e.clientY - startY) < DRAG_THRESHOLD
        ) {
          return;
        }
        armed = true;
      }
      this.panX = originPanX + (e.clientX - startX);
      this.panY = originPanY + (e.clientY - startY);
      this.applyTransform();
    };
    this.installGesture(this.surfaceEl, "is-panning", move, () => {});
  }

  private zoomBy(factor: number, focalX?: number, focalY?: number): void {
    if (!this.surfaceEl) return;
    const rect = this.surfaceEl.getBoundingClientRect();
    const fx = (focalX ?? rect.left + rect.width / 2) - rect.left;
    const fy = (focalY ?? rect.top + rect.height / 2) - rect.top;
    const newZoom = clamp(this.zoom * factor, ZOOM_MIN, ZOOM_MAX);
    if (newZoom === this.zoom) return;
    // Keep the world point under the focal screen point fixed.
    const worldX = (fx - this.panX) / this.zoom;
    const worldY = (fy - this.panY) / this.zoom;
    this.zoom = newZoom;
    this.panX = fx - worldX * newZoom;
    this.panY = fy - worldY * newZoom;
    this.applyTransform();
  }

  private resetView(): void {
    this.setTransform(0, 0, 1, true);
  }

  /** EXPAND → zoom-to-fit so the entire board is visible at once. */
  private fitToView(animate = true): void {
    if (!this.surfaceEl) return;
    const tiles = this.readTiles();
    const rect = this.surfaceEl.getBoundingClientRect();
    const viewW = rect.width || 800;
    const viewH = rect.height || 460;
    if (tiles.length === 0) {
      this.setTransform(0, 0, 1, animate);
      return;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const t of tiles) {
      minX = Math.min(minX, t.extras.x);
      minY = Math.min(minY, t.extras.y);
      maxX = Math.max(maxX, t.extras.x + t.extras.w);
      maxY = Math.max(maxY, t.extras.y + t.extras.h);
    }
    const contentW = Math.max(1, maxX - minX);
    const contentH = Math.max(1, maxY - minY);
    const zx = (viewW - FIT_PADDING * 2) / contentW;
    const zy = (viewH - FIT_PADDING * 2) / contentH;
    let z = Math.min(zx, zy);
    if (!Number.isFinite(z) || z <= 0) z = 1;
    z = clamp(z, ZOOM_MIN, 1.5);
    const panX = (viewW - contentW * z) / 2 - minX * z;
    const panY = (viewH - contentH * z) / 2 - minY * z;
    this.setTransform(panX, panY, z, animate);
  }

  private setTransform(
    panX: number,
    panY: number,
    zoom: number,
    animate: boolean,
  ): void {
    this.panX = panX;
    this.panY = panY;
    this.zoom = clamp(zoom, ZOOM_MIN, ZOOM_MAX);
    if (animate && this.worldEl) {
      this.worldEl.classList.add("is-animating");
      window.setTimeout(
        () => this.worldEl?.classList.remove("is-animating"),
        320,
      );
    }
    this.applyTransform();
  }

  private applyTransform(): void {
    if (this.worldEl) {
      this.worldEl.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
    }
    if (this.zoomLabelEl) {
      this.zoomLabelEl.textContent = `${Math.round(this.zoom * 100)}%`;
    }
  }

  /** World-space coordinate at the center of the current viewport. */
  private viewportCenterWorld(): { x: number; y: number } {
    if (!this.surfaceEl) return { x: 120, y: 120 };
    const rect = this.surfaceEl.getBoundingClientRect();
    const w = rect.width || 800;
    const h = rect.height || 460;
    return {
      x: (w / 2 - this.panX) / this.zoom,
      y: (h / 2 - this.panY) / this.zoom,
    };
  }

  /** Convert a screen (client) point to world coordinates. */
  private screenToWorld(
    clientX: number,
    clientY: number,
  ): { x: number; y: number } {
    if (!this.surfaceEl) return { x: clientX, y: clientY };
    const rect = this.surfaceEl.getBoundingClientRect();
    return {
      x: (clientX - rect.left - this.panX) / this.zoom,
      y: (clientY - rect.top - this.panY) / this.zoom,
    };
  }

  // ------------------------------------------------------------------
  // Drag & resize (world-space, zoom-aware, unbounded)
  // ------------------------------------------------------------------

  private bringToFront(el: HTMLElement): void {
    el.style.zIndex = String(++this.zCounter);
  }

  private beginDrag(
    el: HTMLElement,
    id: string,
    ev: PointerEvent,
    onTap?: () => void,
  ): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.bringToFront(el);
    const tile = this.currentTile(id);
    if (!tile) return;
    const startX = ev.clientX;
    const startY = ev.clientY;
    const originX = tile.extras.x;
    const originY = tile.extras.y;
    let armed = false;

    const move = (e: PointerEvent) => {
      if (!armed) {
        if (
          Math.abs(e.clientX - startX) < DRAG_THRESHOLD &&
          Math.abs(e.clientY - startY) < DRAG_THRESHOLD
        ) {
          return;
        }
        armed = true;
        el.classList.add("is-dragging");
      }
      // Screen delta → world delta (divide by zoom). Infinite — no clamp.
      tile.extras.x = originX + (e.clientX - startX) / this.zoom;
      tile.extras.y = originY + (e.clientY - startY) / this.zoom;
      el.style.left = `${tile.extras.x}px`;
      el.style.top = `${tile.extras.y}px`;
    };
    this.installGesture(
      el,
      "is-dragging",
      move,
      () => {
        if (armed) {
          tile.extras.x = Math.round(tile.extras.x);
          tile.extras.y = Math.round(tile.extras.y);
          this.persistTile(tile);
        } else {
          onTap?.();
        }
      },
      false,
    );
  }

  private beginResize(el: HTMLElement, id: string, ev: PointerEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.bringToFront(el);
    const tile = this.currentTile(id);
    if (!tile) return;
    const startX = ev.clientX;
    const startY = ev.clientY;
    const originW = tile.extras.w;
    const originH = tile.extras.h;

    const move = (e: PointerEvent) => {
      tile.extras.w = Math.max(
        MIN_W,
        originW + (e.clientX - startX) / this.zoom,
      );
      tile.extras.h = Math.max(
        MIN_H,
        originH + (e.clientY - startY) / this.zoom,
      );
      el.style.width = `${tile.extras.w}px`;
      el.style.height = `${tile.extras.h}px`;
    };
    this.installGesture(el, "is-resizing", move, () => {
      tile.extras.w = Math.round(tile.extras.w);
      tile.extras.h = Math.round(tile.extras.h);
      this.persistTile(tile);
    });
  }

  /**
   * Shared pointer gesture loop for drag / resize / pan. `applyClassNow`
   * controls whether the visual state class is applied immediately
   * (resize/pan) or once a threshold is crossed (drag). The class is
   * always removed on release.
   */
  private installGesture(
    el: HTMLElement,
    cls: string,
    onMove: (e: PointerEvent) => void,
    onEnd: () => void,
    applyClassNow = true,
  ): void {
    this.gestureCleanup?.();
    if (applyClassNow) el.classList.add(cls);
    const move = (e: PointerEvent) => onMove(e);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      el.classList.remove(cls);
      this.gestureCleanup = null;
      onEnd();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    this.gestureCleanup = up;
  }

  private currentTile(id: string): Tile | null {
    const items = this.store.get("moodBoardItems");
    const idx = items.findIndex((it) => it.id === id);
    if (idx === -1) return null;
    return this.itemToTile(items[idx], idx);
  }

  // ------------------------------------------------------------------
  // Image upload / drop / paste + palette extraction
  // ------------------------------------------------------------------

  private promptForImage(targetTileId?: string): void {
    if (!this.fileInputEl) return;
    const input = this.fileInputEl;
    input.value = "";
    const handler = () => {
      input.removeEventListener("change", handler);
      const file = input.files?.[0];
      if (file) void this.ingestImageFile(file, targetTileId);
    };
    input.addEventListener("change", handler);
    input.click();
  }

  private wireSurfaceDropZone(surface: HTMLElement): void {
    const setTarget = (on: boolean) =>
      surface.classList.toggle("is-drop-target", on);

    surface.addEventListener("dragover", (ev) => {
      ev.preventDefault();
      setTarget(true);
    });
    surface.addEventListener("dragleave", (ev) => {
      if (ev.target === surface) setTarget(false);
    });
    surface.addEventListener("drop", (ev) => {
      ev.preventDefault();
      setTarget(false);
      const file = ev.dataTransfer?.files?.[0];
      if (file && file.type.startsWith("image/")) {
        void this.ingestImageFile(file, undefined, ev);
      }
    });
    surface.addEventListener("paste", (ev) => {
      const item = Array.from(ev.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith("image/"),
      );
      const file = item?.getAsFile();
      if (file) void this.ingestImageFile(file);
    });
  }

  private async ingestImageFile(
    file: File,
    targetTileId?: string,
    dropEvent?: DragEvent,
  ): Promise<void> {
    const dataUrl = await readFileAsDataUrl(file);
    if (!dataUrl) return;

    if (targetTileId) {
      const tile = this.currentTile(targetTileId);
      if (tile) {
        tile.extras.imageData = dataUrl;
        this.persistTile(tile);
        this.rerenderTileBody(targetTileId);
      }
      return;
    }

    const palette = await extractPalette(dataUrl).catch(() => [] as string[]);
    const title = file.name.replace(/\.[^.]+$/, "") || "Image";

    // Drop position → world coords, so the tile lands under the cursor.
    let dropWorld: { x: number; y: number } | null = null;
    if (dropEvent) {
      const w = this.screenToWorld(dropEvent.clientX, dropEvent.clientY);
      dropWorld = { x: w.x - TILE_DEFAULTS.image.w / 2, y: w.y - 20 };
    }

    const extra: Partial<TileExtras> = { imageData: dataUrl };
    if (dropWorld) {
      extra.x = Math.round(dropWorld.x);
      extra.y = Math.round(dropWorld.y);
    } else {
      const center = this.viewportCenterWorld();
      extra.x = Math.round(center.x - TILE_DEFAULTS.image.w / 2);
      extra.y = Math.round(center.y - TILE_DEFAULTS.image.h / 2);
    }
    this.createTile("image", title, "", extra);

    // Auto-seed a companion palette tile from the extracted colors.
    if (palette.length > 0) {
      this.createTile("palette", `${title} palette`, "", {
        palette,
        x: extra.x ?? 24,
        y: (extra.y ?? 20) + TILE_DEFAULTS.image.h + 12,
      });
    }
  }
}

// ----------------------------------------------------------------------
// Pure helpers
// ----------------------------------------------------------------------

function defaultTitle(kind: MoodBoardItem["kind"]): string {
  switch (kind) {
    case "image":
      return "Image";
    case "palette":
      return "Palette";
    case "lyric":
      return "Dialogue / Lyric";
    default:
      return "Note";
  }
}

function clamp(v: number, lo: number, hi: number): number {
  if (hi < lo) return lo;
  return Math.min(hi, Math.max(lo, v));
}

function numberOr(...candidates: Array<number | undefined>): number {
  for (const c of candidates) {
    if (typeof c === "number" && Number.isFinite(c)) return c;
  }
  return 0;
}

function normalizeHex(hex: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  return m ? `#${m[1].toLowerCase()}` : "#cccccc";
}

/** Derive a short visual subject from a scene heading/body for a gen prompt. */
function sceneSubject(heading: string, body: string): string {
  const slug = (heading || "").replace(/^\s*(int|ext)[.\s-]+/i, "").trim();
  if (slug) return slug.toLowerCase();
  const firstLine = (body || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .find(Boolean);
  return (firstLine ?? "").slice(0, 40).toLowerCase();
}

/** Pull the first dialogue/quoted line from scenes for a lyric anchor. */
function firstDialogue(
  scenes: Array<{
    body: string;
    elements?: Array<{ type?: string; text?: string }>;
  }>,
): string | null {
  for (const scene of scenes) {
    for (const el of scene.elements ?? []) {
      if (el.type === "dialogue" && el.text && el.text.trim()) {
        return el.text.trim().slice(0, 120);
      }
    }
  }
  for (const scene of scenes) {
    const m = /["“]([^"”]{4,120})["”]/.exec(scene.body || "");
    if (m) return m[1];
  }
  return null;
}

/** A deterministic cinematic palette from a seed string (fallback gen). */
function cinematicPalette(seed: string): string[] {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const base = (h >>> 0) % 360;
  const hsl = (deg: number, s: number, l: number) =>
    hslToHex(((deg % 360) + 360) % 360, s, l);
  return [
    hsl(base, 18, 14),
    hsl(base + 12, 32, 34),
    hsl(base + 28, 46, 56),
    hsl(base + 180, 30, 62),
    hsl(base + 40, 22, 88),
  ];
}

function hslToHex(h: number, s: number, l: number): string {
  const sN = s / 100;
  const lN = l / 100;
  const c = (1 - Math.abs(2 * lN - 1)) * sN;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lN - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function readFileAsDataUrl(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

/**
 * Extract a small dominant-color palette from an image data URL using a
 * downscaled canvas + coarse color-bucket quantization. Returns up to
 * `count` hex strings sorted by frequency. Best-effort: resolves to []
 * on any failure (e.g. canvas unavailable in a headless context).
 */
function extractPalette(dataUrl: string, count = 5): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const size = 48;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve([]);
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        const buckets = new Map<
          string,
          { r: number; g: number; b: number; n: number }
        >();
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 125) continue;
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const key = `${r >> 4}-${g >> 4}-${b >> 4}`;
          const bucket = buckets.get(key);
          if (bucket) {
            bucket.r += r;
            bucket.g += g;
            bucket.b += b;
            bucket.n += 1;
          } else {
            buckets.set(key, { r, g, b, n: 1 });
          }
        }
        const sorted = [...buckets.values()]
          .sort((a, b) => b.n - a.n)
          .slice(0, count);
        resolve(
          sorted.map(
            (bk) =>
              "#" +
              [bk.r, bk.g, bk.b]
                .map((c) => Math.round(c / bk.n).toString(16).padStart(2, "0"))
                .join(""),
          ),
        );
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error("image load failed"));
    img.src = dataUrl;
  });
}
